import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2, Loader2, MessageSquare, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, digitsOnly } from "../lib/api";

const INTEREST_OPTIONS = ["2 BHK", "3 BHK"];
const BUDGET_OPTIONS = ["Under ₹60 L", "₹60 L – ₹80 L", "₹80 L – ₹1 Cr", "₹1 Cr – ₹1.3 Cr", "₹1.3 Cr+"];

const inputCls =
  "w-full bg-white border border-[#DCECC0] rounded-lg px-4 py-3 text-sm text-[#365314] placeholder:text-[#65A30D]/50 focus:outline-none focus:border-[#84CC16] focus:ring-2 focus:ring-[#84CC16]/20 transition-all duration-300";

export const LeadForm = ({ source, defaultInterest = "", dark = false, testIdPrefix = "lead-form", whatsapp = "+919163841785" }) => {
  const [form, setForm] = useState({
    full_name: "", phone: "", whatsapp: "", interest: defaultInterest, budget: "", visit_date: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await api.post("/leads", { ...form, source });
      setStatus("success");
      toast.success("Enquiry received. Forest Hill Realtors will reach out shortly.");
    } catch (err) {
      setStatus("error");
      setError(formatApiError(err));
    }
  };

  if (status === "success") {
    const waText = encodeURIComponent(
      `Hello Forest Hill Realtors, I am ${form.full_name}. I just submitted an enquiry for Vinayak 21 Acres${form.interest ? ` (${form.interest})` : ""}. Please share the latest price, availability and floor plans.`
    );
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`text-center py-10 px-6 rounded-xl ${dark ? "bg-[#F4FAE8] border border-[#84CC16]/30" : "bg-[#F4FAE8] border border-[#84CC16]/40"}`}
        data-testid={`${testIdPrefix}-success`}
      >
        <CheckCircle2 size={40} className="text-[#84CC16] mx-auto" strokeWidth={1.4} />
        <p className={`font-serif-display text-2xl mt-5 ${dark ? "text-[#3F6212]" : "text-[#365314]"}`}>Thank you.</p>
        <p className={`text-sm mt-2 leading-relaxed ${dark ? "text-[#4D7C0F]" : "text-[#4D7C0F]"}`}>
          Forest Hill Realtors will contact you shortly regarding Vinayak 21 Acres.
        </p>
        <a
          data-testid={`${testIdPrefix}-whatsapp-cta`}
          href={`https://wa.me/${digitsOnly(whatsapp)}?text=${waText}`}
          target="_blank" rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 bg-[#25D366] text-white font-sub font-medium text-sm px-7 py-3.5 rounded-full hover:bg-[#1EBE57] active:scale-[0.97] transition-all duration-300"
        >
          <MessageSquare size={16} /> Chat with us on WhatsApp
        </a>
        <a
          data-testid={`${testIdPrefix}-brochure-btn`}
          href="/assets/vinayak-21-acres-floor-plan-brochure.pdf"
          download="Vinayak-21-Acres-Floor-Plan-Brochure-Forest-Hill-Realtors.pdf"
          className="mt-3 inline-flex items-center gap-2 bg-[#FFFFFF] border border-[#9CCB58] text-[#3F6212] font-sub font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-[#F4FAE8] active:scale-[0.97] transition-all duration-300"
        >
          <Download size={16} className="text-[#65A30D]" /> Download Floor Plan Brochure (PDF)
        </a>
        <p className="text-[11px] mt-2 text-[#65A30D]">
          All 40 official floor plans — yours to keep.
        </p>
        <p className={`text-[11px] mt-3 ${dark ? "text-[#65A30D]" : "text-[#65A30D]"}`}>
          Get an instant reply — start the conversation now.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid={`${testIdPrefix}-form`}>
      <div className="grid sm:grid-cols-2 gap-3.5">
        <input data-testid={`${testIdPrefix}-name-input`} required minLength={2} placeholder="Full Name *" value={form.full_name} onChange={set("full_name")} className={inputCls} />
        <input data-testid={`${testIdPrefix}-phone-input`} required minLength={8} type="tel" placeholder="Mobile Number *" value={form.phone} onChange={set("phone")} className={inputCls} />
      </div>
      <div className="grid sm:grid-cols-2 gap-3.5">
        <input data-testid={`${testIdPrefix}-whatsapp-input`} type="tel" placeholder="WhatsApp Number" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} />
        <select data-testid={`${testIdPrefix}-interest-select`} value={form.interest} onChange={set("interest")} className={inputCls}>
          <option value="">Interested In</option>
          {INTEREST_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 gap-3.5">
        <select data-testid={`${testIdPrefix}-budget-select`} value={form.budget} onChange={set("budget")} className={inputCls}>
          <option value="">Approximate Budget</option>
          {BUDGET_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input data-testid={`${testIdPrefix}-visitdate-input`} type="date" value={form.visit_date} onChange={set("visit_date")} className={inputCls} aria-label="Preferred Site Visit Date" />
      </div>
      <textarea data-testid={`${testIdPrefix}-message-input`} rows={3} placeholder="Message (optional)" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
      {error && <p className="text-sm text-red-700" data-testid={`${testIdPrefix}-error`}>{error}</p>}
      <button
        data-testid={`${testIdPrefix}-submit-btn`}
        type="submit"
        disabled={status === "loading"}
        className="w-full bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-medium text-sm px-6 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {status === "loading" && <Loader2 size={16} className="animate-spin" />}
        Get Latest Price &amp; Availability
      </button>
      <p className={`text-[11px] text-center leading-relaxed ${dark ? "text-[#65A30D]" : "text-[#65A30D]"}`}>
        By submitting, you agree to be contacted by Forest Hill Realtors regarding Vinayak 21 Acres.
      </p>
    </form>
  );
};

export const LeadModal = ({ open, onClose, source, defaultInterest = "", whatsapp }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-[#F4FAE8]/80 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-6"
        onClick={onClose}
        data-testid="lead-modal-overlay"
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#FFFFFF] w-full max-w-lg rounded-t-3xl sm:rounded-2xl p-6 sm:p-8 max-h-[92svh] overflow-y-auto shadow-2xl"
          data-testid="lead-modal"
        >
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="overline-tag">{source}</p>
              <h3 className="font-serif-display text-2xl sm:text-3xl font-medium text-[#365314] mt-2">Vinayak 21 Acres</h3>
            </div>
            <button data-testid="lead-modal-close-btn" onClick={onClose} className="p-2 text-[#65A30D] hover:text-[#365314] transition-colors" aria-label="Close">
              <X size={20} />
            </button>
          </div>
          <LeadForm source={source} defaultInterest={defaultInterest} testIdPrefix="modal-lead-form" whatsapp={whatsapp} />
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
